import React from "react";
import { MapPin, Star, ShieldCheck, ArrowRight, ArrowDown, Instagram } from "lucide-react";
import { clinicInfo, stats, doctors, interiorImage3 } from "../mock/data";

function FloatingPill({ icon: Icon, label, sub, className = "" }) {
  return (
    <div className={`inline-flex items-center gap-3 bg-[#0e2b2b]/70 backdrop-blur-md rounded-full pl-2 pr-5 py-2 shadow-2xl border border-white/15 ${className}`}>
      <span className="w-8 h-8 rounded-full bg-[#14a68e] text-white flex items-center justify-center shrink-0">
        <Icon size={15} />
      </span>
      <span className="text-[14px] font-bold text-white tracking-[0.05em] whitespace-nowrap">
        {sub && <span className="italic-serif normal-case font-medium text-[#5eead4] mr-1.5 text-[15px]">{sub}</span>}
        {label}
      </span>
    </div>
  );
}

export default function Hero() {
  return (
    <section id="top" className="relative">
      {/* Full-bleed hero */}
      <div className="relative min-h-[86vh] md:min-h-[720px] overflow-hidden bg-[#0a1f1f]">
        {/* Background clinic interior */}
          <picture className="absolute inset-0 w-full h-full">
            <source
              media="(max-width: 800px)"
              srcSet="/images/hero/clinic-hero-800.webp"
            />
            <img
              src={interiorImage3}
              alt="Dr. Khurana's Dental & Aesthetic Clinic interior — Chhatameel, Lucknow"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              width="1600"
              height="1200"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </picture>
        {/* Dark teal gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f1f]/95 via-[#0a1f1f]/70 to-[#0a1f1f]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f1f]/70 via-transparent to-[#0a1f1f]/30" />
        {/* subtle noise / vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_35%,rgba(20,166,142,0.18),transparent_55%)]" />

        {/* Doctor portrait overlay */}
        <img
          src={doctors[0].photo}
          alt={doctors[0].name}
          width={1000}
          height={1207}
          className="hidden lg:block pointer-events-none select-none absolute bottom-0 right-[2%] h-[68%] xl:right-[8%] xl:h-[648px] w-auto object-contain z-10 drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)]"
        />

        {/* Content */}
        <div className="relative z-20 max-w-[1400px] mx-auto px-6 md:px-8 pt-16 md:pt-20 pb-14 min-h-[86vh] md:min-h-[720px] lg:min-h-[600px] xl:min-h-[720px] flex flex-col justify-between">
          {/* Top left: location tag */}
          <div>
            <div className="flex items-center gap-3 text-[14px] md:text-[15px] tracking-[0.24em] uppercase text-[#5eead4] font-bold">
              <span className="w-10 h-px bg-[#5eead4]/70" />
              {clinicInfo.city} · {clinicInfo.established}
            </div>

            <h1 className="mt-6 md:mt-8 font-serif-display text-[42px] sm:text-[56px] md:text-[80px] xl:text-[92px] leading-[0.98] text-white max-w-[820px] lg:max-w-[650px] xl:max-w-[820px]">
              Advanced Dental Care<br />
              for a <span className="italic-serif text-[#5eead4]">Healthier Smile</span>
            </h1>

            <p className="mt-6 md:mt-8 text-[15px] md:text-[17px] text-white/80 max-w-[520px] leading-relaxed">
              Premium dental care with a personal touch. At Dr. Khurana&rsquo;s Dental &amp; Aesthetic Clinic, we combine state&#8209;of&#8209;the&#8209;art technology with compassionate, patient&#8209;first care &mdash; giving every patient in Lucknow the smile they deserve.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 max-w-[560px]">
              <a href="#contact" className="btn-primary">Book an Appointment <ArrowRight size={16} /></a>
              <a href="#treatments" className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[13px] uppercase tracking-wide font-semibold bg-white/10 text-white border border-white/25 hover:bg-white/20 transition backdrop-blur-sm">Our Treatments <ArrowDown size={16} /></a>
              <a href="#reviews" className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[13px] uppercase tracking-wide font-semibold bg-white/10 text-white border border-white/25 hover:bg-white/20 transition backdrop-blur-sm">See Our Results <ArrowRight size={16} /></a>
              <a href={clinicInfo.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[13px] uppercase tracking-wide font-semibold bg-white/10 text-white border border-white/25 hover:bg-white/20 transition backdrop-blur-sm">Instagram <Instagram size={16} /></a>
            </div>

          {/* Mobile pills row */}
          <div className="xl:hidden mt-8 flex flex-wrap gap-2">
            <FloatingPill icon={MapPin} label="LUCKNOW" sub="Chhatameel," />
            <FloatingPill icon={Star} label="5-Star" />
            <FloatingPill icon={ShieldCheck} label="100% Painless" />
          </div>

            <div className="lg:hidden mt-10 flex justify-center -mb-14">
              <img
                src={doctors[0].photo}
                alt={doctors[0].name}
                width={1000}
                height={1207}
                className="pointer-events-none select-none w-[88%] max-w-[320px] md:max-w-[430px] lg:max-w-[580px] h-auto object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)]"
              />
            </div>
          </div>

          {/* Floating pills column - right side */}
          <div className="hidden xl:flex absolute right-6 md:right-10 top-1/2 -translate-y-1/2 flex-col items-end gap-3 z-30">
            <FloatingPill icon={MapPin} label="LUCKNOW" sub="Chhatameel," />
            <FloatingPill icon={Star} label="RATED ON GOOGLE" sub="5-Star" />
            <FloatingPill icon={ShieldCheck} label="PAIN-FREE TREATMENTS" sub="100%" />
          </div>

        </div>
      </div>

      {/* Stats strip below hero */}
      <div className="bg-[#f6f7f4] border-b border-[#0e2b2b]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-10 md:py-14 grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {stats.map((s, i) => (
            <div key={i} className="reveal">
              <div className={`font-serif-display ${s.value === "★★★★★" ? "text-[28px] md:text-[52px]" : "text-[36px] md:text-[52px]"} text-[#0e2b2b] leading-none`}>
                {s.value}
              </div>
              <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[#0e2b2b]/60 font-semibold">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
